import { getMemberBirthCalendarParts } from "./family-tree/dateAndTime";
import { isDashboardRelatedMember } from "./dashboardMemberDisplayName";

/**
 * The milestone-ages reports list the family, not the in-laws who married into
 * it: an in-law's milestone birthday is carried by their own family's tree. The
 * couple at the head of a line stay, as everywhere else the dashboard counts
 * "related" members — they head the line the report reads down from.
 */
export function filterAgeMilestoneMembers(members) {
  return (Array.isArray(members) ? members : []).filter(isDashboardRelatedMember);
}

/**
 * The age-milestone groups out of a /dashboard-statistics-with-details payload.
 *
 * The API already groups the members by milestone age — each entry is
 * `{ milestone, count, details: [...] }` — so nothing is computed here beyond
 * dropping the in-laws (see filterAgeMilestoneMembers) and the groups they
 * empty; this only reads the block, whose key has been spelled a few different
 * ways across payload versions. The group's own `count` is restated off the
 * rows that survive, so it cannot disagree with the list under it.
 *
 * Shared by both Milestone Ages reports and by the dashboard cards that open
 * them, so a card's number and its page's list come off one read.
 */
export function extractAgeMilestoneGroups(data) {
  const groups =
    data?.age_milestones?.details ||
    data?.ageMilestones?.details ||
    data?.ages_milestone?.details ||
    data?.agesMilestone?.details ||
    [];

  if (!Array.isArray(groups)) return [];

  return groups
    .filter((group) => group?.milestone && group?.details?.length > 0)
    .map((group) => {
      const details = filterAgeMilestoneMembers(group.details);
      return { ...group, count: details.length, details };
    })
    .filter((group) => group.details.length > 0);
}

/**
 * How many members reach a milestone age this year — the total the yearly
 * report opens on. The statistics block carries a count of its own, but it
 * counts the in-laws the report leaves out, so the card counts the same rows
 * the page lists instead.
 */
export function countAgeMilestonesThisYear(data) {
  return extractAgeMilestoneGroups(data).reduce(
    (total, group) => total + (group.details?.length || 0),
    0
  );
}

/**
 * How many members reach a milestone age in the given month (0–11).
 *
 * A member with no date of birth cannot be placed in a month, so they are left
 * out — the monthly report lists exactly these people.
 */
export function countAgeMilestonesInMonth(data, monthIndex) {
  return extractAgeMilestoneGroups(data).reduce(
    (total, group) =>
      total +
      (group.details || []).filter(
        (member) => getMemberBirthCalendarParts(member)?.month === monthIndex
      ).length,
    0
  );
}
